import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useState } from 'react'

import { SidebarContent, SidebarProvider } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { AccountContent } from './AccountContent'
import { DEFAULT_ORG_NAME, Viewport } from './accountModel'
import { ChatPane } from './ChatPane'
import { NavRows } from './NavRows'
import { navGroups, ProductTab, TAB_ENTITY } from './prototypeNav'
import { TopProductNav } from './TopProductNav'

/**
 * The agent as the first column of the account rather than something laid over
 * it: pinned open down the left, with the products moved up into a top bar and
 * the selected one's rows in a nav beside the page.
 */
export function AiForwardPlacement({
  notificationId,
  className,
}: {
  notificationId: number
  className?: string
}) {
  const [tab, setTab] = useState<ProductTab>(ProductTab.INSURANCE)
  const [page, setPage] = useState<string | null>(null)
  // Collapsed by default: under this placement the chat is the column that earns the room.
  const [navOpen, setNavOpen] = useState(false)

  // Dashboard is a mode, not a row: it spans the products, so no product's nav belongs beside it.
  function selectProduct(next: ProductTab) {
    setTab(next)
    setPage(navGroups(next)[0]?.rows[0]?.title ?? null)
  }

  return (
    // min-h-0 overrides the provider's min-h-svh so the pane keeps its own height.
    <SidebarProvider
      // Drives the provider's own collapsed state, which is what gates the row
      // tooltips — without it SidebarMenuButton reads "expanded" and hides them.
      open={navOpen}
      onOpenChange={setNavOpen}
      className={cn('border-ink-100 block min-h-0 overflow-hidden border bg-white', className)}
    >
      <div className="flex h-full flex-col">
        <TopProductNav
          tab={tab}
          onSelect={selectProduct}
          orgName={DEFAULT_ORG_NAME}
          page={page}
          onNavigate={setPage}
        />

        <div className="flex min-h-0 flex-1">
          {page !== null && (
            <div
              className={cn(
                'border-ink-100 flex shrink-0 flex-col border-r',
                navOpen ? 'w-40' : 'w-(--sidebar-width-icon)'
              )}
            >
              <SidebarContent className="no-scrollbar gap-0">
                <NavRows tab={tab} page={page} onNavigate={setPage} collapsed={!navOpen} />
                {navOpen && (
                  <p className="text-ink-300 mt-auto truncate px-4 py-3 text-[10px] leading-4">
                    {TAB_ENTITY[tab]}
                  </p>
                )}
              </SidebarContent>
              <button
                type="button"
                onClick={() => setNavOpen((was) => !was)}
                aria-label={navOpen ? 'Collapse navigation' : 'Expand navigation'}
                aria-expanded={navOpen}
                className="hover:bg-sidebar-accent mt-auto flex items-center justify-center py-2"
              >
                {navOpen ? (
                  <PanelLeftClose className="text-ink-300 size-4" />
                ) : (
                  <PanelLeftOpen className="text-ink-300 size-4" />
                )}
              </button>
            </div>
          )}

          <div className="min-w-0 flex-1 overflow-hidden">
            <AccountContent viewport={Viewport.DESKTOP} page={page} />
          </div>

          <ChatPane notified={notificationId > 0} className="border-ink-100 border-l" />
        </div>
      </div>
    </SidebarProvider>
  )
}
